import type {Model} from '@github-ui/marketplace-common'
import type {
  ImageInputs,
  MessageContent,
  ModelInputSchema,
  PlaygroundAPIMessageAuthor,
  PlaygroundMessage,
} from '../types'
import {isImageAttachmentSupported, reachedMaxAttachments, supportImageWithoutText} from './image-validation'
import type {EnabledFeatures} from '@github-ui/react-core/use-feature-flag'

export const textFromMessageContent = (message: MessageContent) => {
  if (typeof message === 'string') {
    return message
  } else {
    const text = message.find(m => m.type === 'text')
    return text?.text ?? ''
  }
}

export const getValidMessage = (
  text: string,
  attachments: string[],
  currentMessages: PlaygroundMessage[],
  modelInputSchema: ModelInputSchema,
  catalogData: Model,
  featureFlags: EnabledFeatures = {},
): PlaygroundMessage | undefined => {
  const hasMessage = text.trim().length > 0
  const hasAttachments = attachments.length > 0
  const allowAttachments =
    isImageAttachmentSupported(catalogData, featureFlags) && !reachedMaxAttachments(currentMessages, modelInputSchema)
  const allowImageOnly = supportImageWithoutText(catalogData.name)

  if (!hasMessage && (!hasAttachments || !allowAttachments || !allowImageOnly)) return

  if (hasAttachments && allowAttachments) {
    // Some models have a limit on the number of images that can be sent in a single message
    const {imagesPerTurn = Infinity} = modelInputSchema?.capabilities?.chat ?? {imagesPerTurn: Infinity}

    const imageInputs = attachments
      .slice(0, imagesPerTurn)
      .map(url => ({type: 'image_url', image_url: {url}}) as ImageInputs)

    if (!hasMessage) return createUserMessage(imageInputs)

    return createUserMessage([...imageInputs, {type: 'text', text}])
  }

  return createUserMessage(text)
}

const createMessage = (role: PlaygroundAPIMessageAuthor, message: MessageContent): PlaygroundMessage => ({
  timestamp: new Date(),
  role,
  message,
})

export const createUserMessage = (message: MessageContent): PlaygroundMessage => createMessage('user', message)
export const createErrorMessage = (message: MessageContent): PlaygroundMessage => createMessage('error', message)
